import { CheckIcon, MusicNoteIcon } from "@/assets/icons";
import type { UploadQueueItem } from "@/components/providers/AudiolabProvider/AudiolabContext";
import UploadDropArea from "@/components/ui/UploadDropArea";
import { cn, formatFileSize } from "@/lib/utils";
import { Flex, Text } from "@radix-ui/themes";
import { useDropzone, type Accept, type FileRejection } from "react-dropzone";

type Step3FileUploadProps = {
  queue: UploadQueueItem[];
  onFilesSelect: (files: File[]) => void;
  errorMessage?: string;
  /** How many files this batch may still contain (batch cap or quota). */
  maxFiles: number;
  /** True while any file of the batch is still in flight. */
  isUploading: boolean;
};

const AUDIO_ACCEPT: Accept = {
  "audio/wav": [".wav"],
  "audio/x-wav": [".wav"],
  "audio/mpeg": [".mp3"],
  "audio/aiff": [".aif", ".aiff"],
  "audio/x-aiff": [".aif", ".aiff"],
};

const AUDIO_MAX_SIZE = 200 * 1024 * 1024;

// Everything the pipeline produces automatically after an upload.
const AUTO_ASSETS = [
  "Clean version",
  "Lyric video",
  "Stems",
  "Lyrics",
  "Instrumental",
  "Track details",
];

const STATUS_LABEL: Record<UploadQueueItem["status"], string> = {
  pending: "Waiting...",
  uploading: "Uploading...",
  creating: "Finishing up...",
  done: "Uploaded",
  error: "Failed",
};

function QueueRow({ item }: { item: UploadQueueItem }) {
  const isDone = item.status === "done";
  const isError = item.status === "error";

  return (
    <Flex align="center" gap="3" data-testid="upload-file-row">
      <Flex
        align="center"
        justify="center"
        className={cn(
          "h-10 w-10 shrink-0 rounded-xl",
          isError
            ? "bg-error-bold/10 text-error-bold"
            : "bg-blue-200/60 text-blue-600",
        )}
      >
        {isDone ? (
          <CheckIcon className="h-5 w-5 fill-current" />
        ) : (
          <MusicNoteIcon className="h-5 w-5" />
        )}
      </Flex>

      <Flex direction="column" gap="1" className="min-w-0 flex-1">
        <Flex align="center" gap="2" justify="between">
          <Text className="truncate font-open-sans text-sm font-semibold text-blue-900">
            {item.file.name}
          </Text>
          <Text className="shrink-0 font-open-sans text-xs text-gray-600">
            {formatFileSize(item.file.size)}
          </Text>
        </Flex>

        {/* A finished or failed row has nothing left to track, so the bar makes
            way for its outcome. */}
        {!isDone && !isError && (
          <Flex align="center" gap="3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-200/70">
              <div
                className="h-full rounded-full bg-blue-600 transition-[width]"
                style={{ width: `${item.progress}%` }}
              />
            </div>
            <Text className="shrink-0 font-open-sans text-xs font-semibold text-blue-600">
              {item.progress}%
            </Text>
          </Flex>
        )}

        <Text
          className={cn(
            "font-open-sans text-xs",
            isError ? "text-error-bold" : "text-gray-600",
          )}
        >
          {isError
            ? (item.errorMessage ?? STATUS_LABEL.error)
            : STATUS_LABEL[item.status]}
        </Text>
      </Flex>
    </Flex>
  );
}

export default function Step3FileUpload({
  queue,
  onFilesSelect,
  errorMessage,
  maxFiles,
  isUploading,
}: Step3FileUploadProps) {
  const hasQueue = queue.length > 0;
  const isFull = maxFiles <= 0;
  const canPick = !isUploading && !isFull;

  const dropzone = useDropzone({
    // Rejections come through too: a 300MB file or a stray PDF is worth an
    // explanation, and dropzone's silent filtering gives none.
    onDrop: (accepted: File[], rejected: FileRejection[]) => {
      const files = [...accepted, ...rejected.map((r) => r.file)];
      if (files.length > 0) onFilesSelect(files);
    },
    accept: AUDIO_ACCEPT,
    maxSize: AUDIO_MAX_SIZE,
    // Non-Pro accounts upload one track at a time, so the picker shouldn't
    // even offer a multi-selection.
    multiple: maxFiles > 1,
    // While the batch is in flight the dropzone is a progress readout.
    disabled: !canPick,
    noClick: !canPick,
    noDrag: !canPick,
  });

  return (
    <Flex direction="column" gap="5">
      <UploadDropArea
        dropzone={dropzone}
        interactive={canPick}
        title={
          maxFiles > 1 ? "Drag your tracks here or" : "Drag your track here or"
        }
        dropTitle={
          maxFiles > 1 ? "Drop your tracks here" : "Drop your track here"
        }
        requirements={`WAV, AIFF or MP3 • Stereo or mono • 200MB max • ${
          maxFiles === 1
            ? "1 file at a time"
            : `up to ${maxFiles} files at a time`
        }`}
        testId="upload-file-dropzone"
        inputTestId="upload-file-input"
        browseTestId="upload-file-browse"
      >
        {hasQueue && (
          <Flex direction="column" gap="4">
            <Flex
              direction="column"
              gap="4"
              // 20 rows at ~66px would push the modal past the viewport.
              className="max-h-[300px] overflow-y-auto pr-1"
            >
              {queue.map((item) => (
                <QueueRow key={item.id} item={item} />
              ))}
            </Flex>

            {/* Once nothing is in flight the dropzone is live again, so a batch
                that partly failed can be retried without reopening the modal. */}
            {canPick && (
              <Text className="font-open-sans text-sm font-semibold text-text-secondary">
                {dropzone.isDragActive ? (
                  "Drop to replace this selection"
                ) : (
                  <>
                    <span className="md:hidden">
                      {maxFiles > 1
                        ? "Tap to pick a new selection"
                        : "Tap to pick a new one"}
                    </span>
                    <span className="hidden md:inline">
                      {maxFiles > 1
                        ? "Drag more tracks here or click to pick a new selection"
                        : "Drag another track here or click to pick a new one"}
                    </span>
                  </>
                )}
              </Text>
            )}
          </Flex>
        )}
      </UploadDropArea>

      {errorMessage && (
        <Text
          className="font-open-sans text-xs text-error-bold"
          data-testid="upload-file-error"
        >
          {errorMessage}
        </Text>
      )}

      <Flex direction="column" gap="3">
        <Text className="font-open-sans text-sm text-blue-900">
          After uploading, every track also gets these assets automatically:
        </Text>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          {AUTO_ASSETS.map((asset) => (
            <Flex key={asset} align="center" gap="2">
              <CheckIcon className="h-4 w-4 shrink-0 fill-current text-blue-600" />
              <Text className="font-open-sans text-sm text-blue-900">
                {asset}
              </Text>
            </Flex>
          ))}
        </div>
      </Flex>
    </Flex>
  );
}
